import { useMutation } from '@apollo/client';
import { FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import invariant from 'tiny-invariant';
import { graphql } from '~/apollo/generated/v3';
import { PageHeading } from '~/components/common/PageHeading';
import { Panel } from '~/components/common/Panel';
import { useBreadcrumb } from '~/components/layout/Breadcrumb';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { ProjectFormFields } from '~/components/upload/project/ProjectFormFields';
import { uploadProjectUpdateRoute } from '~/paths';
import { useZodForm } from '~/utils/forms';
import { initialProject, projectSchema } from '~/utils/modules/project';

export const CREATE_PROJECT = graphql(`
  mutation createProject($project: ProjectInput!) {
    createProject(project: $project) {
      id
    }
  }
`);

export default function UploadProjectCreatePage() {
  useBreadcrumb('routes/upload/model/project/create', 'Create Project');
  const navigate = useNavigate();
  const [createProject, { loading, error }] = useMutation(CREATE_PROJECT);

  const form = useZodForm({
    schema: projectSchema,
    values: initialProject(),
  });

  const handleSubmit = form.handleSubmit(async values => {
    try {
      const res = await createProject({
        variables: {
          project: values,
        },
      });
      invariant(res.data);
      const projectId = res.data.createProject.id;
      navigate(uploadProjectUpdateRoute(projectId));
      toast.success('Project created successfully.');
    } catch (err) {
      console.log('Error saving project', err);
      toast.error('There was a problem saving the project.');
    }
  });

  return (
    <>
      <PageHeading>Create Project</PageHeading>

      <FormProvider {...form}>
        <form onSubmit={handleSubmit}>
          <Panel>
            <Panel.Heading>
              <Panel.Title>Create Project</Panel.Title>
            </Panel.Heading>

            <Panel.Body className="space-y-4">
              <ProjectFormFields />
              <HookFormErrors graphQLError={error} />
            </Panel.Body>

            <Panel.Footer align="right">
              <Button type="submit" color="primary" loading={loading}>
                Save
              </Button>
            </Panel.Footer>
          </Panel>
        </form>
      </FormProvider>
    </>
  );
}
